import React from 'react';
import classnames from 'classnames';
import { compose } from 'redux';

import useResourceQuery from 'dpl/shared/fetching/hooks/useResourceQuery';
import { BREED_ID, LITTER_ID } from 'dpl/constants/query_params';
import useCurrentUser from 'dpl/common/hooks/useCurrentUser';
import { EVENT_CATEGORIES, EVENT_ACTION_IDS } from 'dpl/constants/analytics';
import { trackEvent, trackConversion } from 'dpl/util/analytics';
import { get } from 'dpl/shared/utils/object';
import Icon from 'dpl/common/components/Icon';
import { breederProfileAvailabilityResourceDefinition } from 'dpl/shared/utils/tptConnectResourceDefinitions';
import Button from 'dpl/common/design_system/Button';

interface ModalApplyButtonProps {
  breederProfileUrl: string;
  breederProfileId: number;
  breedId?: number | string;
  litterId?: number | null;
  analytics?: {
    breeder_preferred_status?: string;
    page_number?: number;
    position?: number;
    view?: string;
  };
  className?: string;
  withIcon?: boolean;
  customCopy?: string | null;
}

export default function ModalApplyButton({
  breederProfileUrl,
  breederProfileId,
  breedId = '',
  litterId = null,
  analytics = {},
  className = 'pv3 w-100 flex justify-center mt3 mb6',
  withIcon = false,
  customCopy = null
}: ModalApplyButtonProps) {
  const currentUser = useCurrentUser();

  const handleApplyClick = compose(
    trackEvent(EVENT_CATEGORIES.APPLY_TO_BREEDER),
    trackConversion(EVENT_ACTION_IDS.APPLY_TO_BREEDER)
  )(() => {
    const breederProfileEndpoint = breederProfileUrl.split('#')[0];
    const view = analytics?.view ?? '';

    const litterParam = litterId ? `&${LITTER_ID}=${litterId}` : '';

    window.location.href = `${breederProfileEndpoint}/contacts/buyer_application?view=${view}&${BREED_ID}=${breedId}${litterParam}`;
  });

  const breederAvailabilityData = useResourceQuery(
    breederProfileAvailabilityResourceDefinition(breederProfileId)
  );

  const {
    value: {
      data: { breeds }
    }
  } = breederAvailabilityData;

  const availableBreeds = breeds.filter(
    (b: any) => !b.snoozed && (breedId ? b.id === breedId : true)
  );

  const appliedTo = get(
    currentUser,
    'value.data.breeder_profiles_applied_to',
    []
  );

  const canApply =
    typeof currentUser?.isLoggedIn === 'boolean' &&
    !appliedTo.includes(breederProfileId);

  if (availableBreeds.length === 0 || !canApply) {
    return null;
  }

  const defaultCopy = (
    <>
      Apply&nbsp;
      <span className="d-none d-sm-inline">to Breeder</span>
    </>
  );

  return (
    <Button
      className={classnames('ModalApplyButton font-16', className)}
      onClick={handleApplyClick}
      size="default"
    >
      {withIcon && <Icon className="mr3" name="application" />}
      {customCopy ? <span>{customCopy}</span> : defaultCopy}
    </Button>
  );
}
